import { useContext, useEffect } from 'react'
import { useStore } from 'zustand'

import { useAsyncError } from '@/modules/errors'
import type { ProjectStore } from '@/modules/projects'
import { byFavorite } from '@/utils'
import { ProjectContext } from '../components/ProjectStoreContext'
import { flaggedCheckFiles, securitiesReadState } from '../status'

const useProjectStore = <T>(selector: (state: ProjectStore) => T): T => {
  const stores = useContext(ProjectContext)
  const store = stores?.projectStore

  if (!store) {
    throw new Error('useProjectStore must be used within ProjectProvider')
  }

  return useStore(store, selector)
}

export const useProjectMutating = () =>
  useProjectStore((state) => state.mutating)

export const useLoading = () => {
  const isLoadingStatements = useLoadingStatements()
  const isLoadingTransactions = useLoadingTransactions()

  return isLoadingStatements || isLoadingTransactions
}

export const useProject = () => useProjectStore((state) => state.project)

export const useLoadingProject = () =>
  useProjectStore((state) => state.loadingProject)

export const useStatements = () => useProjectStore((state) => state.statements)

export const useLoadingStatements = () => {
  const isLoadingProject = useLoadingProject()
  const isLoadingStatements = useProjectStore(
    (state) => state.loadingStatements,
  )
  return isLoadingProject || isLoadingStatements
}

// Returns statement-side transactions only. Check-image rows live in the
// dedicated checks slice (`useChecks` / `useCheckTransactions`) backed by
// /projects/:slug/checks on lukita; they aren't mixed into this list.
export const useTransactions = () => {
  const transactions = useProjectStore((state) => state.transactions)
  const filterByFavorites = useProjectStore((state) => state.filterByFavorites)
  return filterByFavorites ? transactions.filter(byFavorite) : transactions
}

// Same store, but ignores the favorites filter. The chat's
// TransactionsArtifact uses this — when the model named specific rows
// to show, those rows must appear regardless of the user's current
// favorites toggle.
export const useAllTransactions = () =>
  useProjectStore((state) => state.transactions)

export const useCheckTransactions = () =>
  useProjectStore((state) => state.checks)

// The project's resolved account directory: the voted owner, institution and type per account. Label
// an account from here, not from a transaction row, so one bad row can't rename the whole account.
export const useDirectoryAccounts = () =>
  useProjectStore((state) => state.directoryAccounts)

export const useEdits = () => useProjectStore((state) => state.edits)

// True when any extracted row belongs to an investment or retirement account.
// Reads the unfiltered list so the favorites toggle can't hide the tab.
export const useHasInvestmentAccounts = () => {
  const transactions = useAllTransactions()
  return transactions.some(
    (transaction) =>
      transaction.accountType === 'Investment' ||
      transaction.accountType === 'Retirement',
  )
}

// Check files with a checks cycle that needs review, often for pages that could not be read.
export const useFlaggedCheckFiles = () => flaggedCheckFiles(useStatements())

// Where the project's brokerage read stands. See securitiesReadState for the rules.
export const useSecuritiesReadState = () => {
  const statements = useStatements()
  const isBrokerageEnabled = useProject()?.isBrokerageEnabled ?? false
  return securitiesReadState(statements, isBrokerageEnabled)
}

// Whether the Portfolio tab shows. lukita's project flag lands before the statements, which are slow
// on a big brokerage case, so it wins. Without the flag (an older lukita, or a response that leaves it
// out) the statements decide, as they did before.
export const useHasPortfolio = () => {
  const projectHasPortfolio = useProject()?.hasPortfolio
  const securitiesReadState = useSecuritiesReadState()
  return projectHasPortfolio ?? securitiesReadState !== 'none'
}

// True until every page has arrived, so totals and insights never compute from part of the list.
export const useLoadingTransactions = () => {
  const isLoadingFirstPage = useLoadingFirstTransactions()
  const isLoadingMore = useLoadingMoreTransactions()
  return isLoadingFirstPage || isLoadingMore
}

// True only until the first page lands; the ledger can show rows from there.
export const useLoadingFirstTransactions = () => {
  const isLoadingProject = useLoadingProject()
  const isLoadingTransactions = useProjectStore(
    (state) => state.loadingTransactions,
  )
  return isLoadingProject || isLoadingTransactions
}

export const useLoadingMoreTransactions = () =>
  useProjectStore((state) => state.loadingMoreTransactions)

export const useLoadingChecks = () => {
  const isLoadingProject = useLoadingProject()
  const isLoadingChecks = useProjectStore(
    (state) => state.loadingChecks || state.loadingMoreChecks,
  )
  return isLoadingProject || isLoadingChecks
}

export const useLoadingDirectoryAccounts = () => {
  const isLoadingProject = useLoadingProject()
  const isLoadingDirectoryAccounts = useProjectStore(
    (state) => state.loadingDirectoryAccounts,
  )
  return isLoadingProject || isLoadingDirectoryAccounts
}

export const useFilterTransactionsByFavorites = () =>
  useProjectStore((state) => state.filterByFavorites)

export const useProjectActions = () => useProjectStore((state) => state.actions)

export const useProjectMutations = () =>
  useProjectStore((state) => state.mutations)

export const useProjectLoaders = () => useProjectStore((state) => state.loaders)

export const useFetchTransactions = (slug: string) => {
  const { fetchTransactions } = useProjectLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchTransactions(slug).catch(throwError)
  }, [])
}

export const useFetchChecks = (slug: string) => {
  const { fetchChecks } = useProjectLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchChecks(slug).catch(throwError)
  }, [])
}

// No throwError here: the loader swallows its own failure so a missing directory costs the labels,
// not the page.
export const useFetchDirectoryAccounts = (slug: string) => {
  const { fetchDirectoryAccounts } = useProjectLoaders()

  useEffect(() => {
    fetchDirectoryAccounts(slug)
  }, [])
}

export const useFetchProject = (slug: string) => {
  const { fetchProject, fetchStatements } = useProjectLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchProject(slug).catch(throwError)
    fetchStatements(slug).catch(throwError)
  }, [])
}
